import { Controller } from "@hotwired/stimulus"

// Fil de notifications (offcanvas) : animations quand on marque une notification lue / non
// lue, ou tout comme lu, et réaction immédiate à la bascule « Non lues / Toutes ».
//
// Le contrôleur vit sur la turbo-frame de l'offcanvas, pas dans son contenu : chaque action
// re-rend la frame, un contrôleur posé à l'intérieur serait détruit en pleine animation.
//
// L'animation de sortie démarre dès l'envoi du formulaire, en parallèle de la requête ; le
// rendu de la frame est mis en pause (`turbo:before-frame-render` + `resume`) jusqu'à ce
// qu'elle soit finie. Le nouveau contenu arrive alors dans l'état que l'animation a dessiné.
export default class extends Controller {
  static LEAVE_MS = 200
  static COLLAPSE_MS = 220
  static MARK_READ_MS = 260
  static STAGGER_MS = 45
  static MAX_STAGGERED = 10

  // Les deux tenues d'une DepozenUi::FilterPillComponent, telles que le serveur les écrit.
  static PILL_ACTIVE_CLASSES = ["depozen-ui-filter-pill--active", "bg-primary", "text-white"]
  static PILL_INACTIVE_CLASSES = ["depozen-ui-filter-pill--inactive", "text-dark"]

  initialize() {
    this.onClick = (event) => this.clicked(event)
    this.onSubmitStart = (event) => this.submitStarted(event)
    this.onSubmitEnd = (event) => this.submitEnded(event)
    this.onBeforeRender = (event) => this.beforeRender(event)
    this.onRender = (event) => this.rendered(event)
  }

  connect() {
    this.element.addEventListener("turbo:click", this.onClick)
    this.element.addEventListener("turbo:submit-start", this.onSubmitStart)
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd)
    this.element.addEventListener("turbo:before-frame-render", this.onBeforeRender)
    this.element.addEventListener("turbo:frame-render", this.onRender)
  }

  disconnect() {
    this.element.removeEventListener("turbo:click", this.onClick)
    this.element.removeEventListener("turbo:submit-start", this.onSubmitStart)
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    this.element.removeEventListener("turbo:before-frame-render", this.onBeforeRender)
    this.element.removeEventListener("turbo:frame-render", this.onRender)
  }

  // --- Bascule « Non lues / Toutes » ------------------------------------------------------
  //
  // Une pastille est un lien qui recharge toute la frame : rien ne bougeait à l'écran pendant
  // l'aller-retour, pas même la pastille cliquée, et l'on croyait avoir raté le clic. Même
  // remède que l'activité récente du tableau de bord (utils/recent_activity_skeleton.ts) : la
  // pastille s'allume tout de suite, et la liste cède la place au gabarit que le serveur a
  // préparé pour ce filtre (cf. Notifications::FeedComponent) — un squelette d'autant de
  // lignes qu'on en attend, ou l'état vide quand on sait déjà qu'il n'y a rien.
  //
  // Rien à défaire : la réponse remplace tout le contenu de la frame, pastilles comprises, et
  // un échec de chargement aussi (`showTurboFrameLoadError`, cf. entrypoints/common). Un
  // second clic avant la réponse reprend simplement la main : Turbo annule la requête en vol.
  //
  // `turbo:click` plutôt que `click` : Turbo ne l'émet que pour les liens qu'il prend en
  // charge (pas pour un Cmd+clic, qui ouvre un onglet). Émis sur le lien, il traverse la frame
  // avant d'arriver au document, où l'intercepteur de la frame l'attend pour lancer la
  // navigation — le lien doit donc rester en place : on le rhabille, on ne le remplace pas.
  clicked(event) {
    const pill = event.target.closest?.("a[data-feed-filter]")
    const feed = this.feed
    if (!pill || !feed?.contains(pill)) return

    const placeholder = this.placeholderFor(pill.dataset.feedFilter)
    const region = this.contentRegion
    if (!placeholder || !region) return

    this.markFilterActive(pill)
    region.replaceWith(placeholder.content.cloneNode(true))
    feed.setAttribute("aria-busy", "true")

    // « Tout marquer comme lu » porte l'ANCIEN filtre dans son URL : envoyé maintenant, il
    // ramènerait sur la liste qu'on vient de quitter, en course avec la requête en vol.
    feed.querySelector("[data-feed-action='mark-all']")?.setAttribute("inert", "")

    // Une nouvelle liste se lit depuis le haut. La barre d'outils est collante : on pouvait
    // cliquer du bas d'un long fil, et le squelette serait resté hors champ.
    const scroller = this.element.closest(".offcanvas-body")
    if (scroller) scroller.scrollTop = 0

    this.focusedFilter = document.activeElement === pill ? pill.dataset.feedFilter : null
  }

  // Le lien qui avait le focus est parti avec l'ancien contenu : sans ça, le focus retombait
  // sur <body> et le Tab suivant repartait du haut de la page.
  restoreFilterFocus() {
    const filter = this.focusedFilter
    this.focusedFilter = null
    if (!filter || document.activeElement !== document.body) return

    this.element.querySelector(`a[data-feed-filter="${CSS.escape(filter)}"]`)?.focus()
  }

  markFilterActive(pill) {
    const { PILL_ACTIVE_CLASSES, PILL_INACTIVE_CLASSES } = this.constructor

    this.element.querySelectorAll("a[data-feed-filter]").forEach((other) => {
      const active = other === pill
      other.classList.remove(...(active ? PILL_INACTIVE_CLASSES : PILL_ACTIVE_CLASSES))
      other.classList.add(...(active ? PILL_ACTIVE_CLASSES : PILL_INACTIVE_CLASSES))
    })
  }

  // --- Marquer lu / non lu ----------------------------------------------------------------

  submitStarted(event) {
    const form = event.target
    const action = form.dataset.feedAction
    if (!action || this.reducedMotion) return

    this.animatedAction = action
    const filter = this.feed?.dataset.filter

    if (action === "mark-all") {
      this.pending = filter === "unread" ? this.leaveAll(this.itemsAndDays) : this.markAllRead(this.unreadItems)
      return
    }

    if (action === "mark-day") {
      const day = form.dataset.feedDay
      if (!day) return

      const items = [...this.element.querySelectorAll(`[data-group="${CSS.escape(day)}"] .notifications-feed__item`)]
      const unreadItems = items.filter((item) => item.classList.contains("notifications-feed__item--unread"))
      this.pending = filter === "unread" ? this.leaveAll(items) : this.markAllRead(unreadItems)
      return
    }

    const item = form.closest(".notifications-feed__item")
    if (!item) return

    const unread = item.classList.contains("notifications-feed__item--unread")
    if (unread && filter === "unread") {
      this.pending = this.leave(item)
    } else if (unread) {
      this.pending = this.markRead(item)
    } else {
      // Retour en non lue : l'état n'existe qu'après le rendu, on l'anime à l'arrivée.
      this.justUnreadId = item.dataset.notificationId
    }
  }

  // Requête en échec : pas de rendu à attendre, on remet les lignes dans leur état.
  submitEnded(event) {
    if (event.detail.success) return

    this.pending = null
    this.justUnreadId = null
    this.element.querySelectorAll(".is-leaving, .is-collapsing, .is-marking-read").forEach((el) => {
      el.classList.remove("is-leaving", "is-collapsing", "is-marking-read")
      el.style.height = ""
    })
  }

  async beforeRender(event) {
    if (event.target !== this.element || !this.pending) return

    event.preventDefault()
    const pending = this.pending
    this.pending = null
    await pending
    event.detail.resume()
  }

  rendered(event) {
    if (event.target !== this.element) return

    this.restoreFilterFocus()
    if (!this.animatedAction) return
    this.animatedAction = null

    this.element.querySelector(".notifications-feed__empty")?.classList.add("is-entering")

    if (this.justUnreadId) {
      const item = this.element.querySelector(`[data-notification-id="${this.justUnreadId}"]`)
      item?.classList.add("is-just-unread")
      this.justUnreadId = null
    }
  }

  // --- Animations -------------------------------------------------------------------------

  // La ligne glisse et s'efface, puis sa hauteur se replie : les suivantes remontent au lieu
  // de sauter. Un jour vidé de ses lignes replie aussi son intitulé.
  async leave(item, delay = 0) {
    await wait(delay)
    item.classList.add("is-leaving")
    await wait(this.constructor.LEAVE_MS)

    const list = item.parentElement
    const dayRow = list?.closest(".notifications-feed__day-body")?.previousElementSibling
    const dayEmptied = list && !list.querySelector(".notifications-feed__item:not(.is-leaving)")

    await Promise.all([
      this.collapse(item),
      dayEmptied && dayRow?.classList.contains("notifications-feed__day-row") ? this.collapse(dayRow) : null
    ])
  }

  // « Tout marquer comme lu » dans « Non lues » : sortie en cascade, sans repli — la liste
  // entière cède la place à l'état vide. Les intitulés de jour sortent dans le même mouvement,
  // à leur rang dans la page : restés seuls à l'écran, ils flottaient au-dessus du vide.
  async leaveAll(items) {
    const { STAGGER_MS, MAX_STAGGERED, LEAVE_MS } = this.constructor
    items.forEach((item, index) => {
      setTimeout(() => item.classList.add("is-leaving"), Math.min(index, MAX_STAGGERED) * STAGGER_MS)
    })
    await wait(Math.min(items.length - 1, MAX_STAGGERED) * STAGGER_MS + LEAVE_MS)
  }

  async markRead(item, delay = 0) {
    await wait(delay)
    item.classList.add("is-marking-read")
    await wait(this.constructor.MARK_READ_MS)
  }

  async markAllRead(items) {
    const { STAGGER_MS, MAX_STAGGERED } = this.constructor
    await Promise.all(items.map((item, index) => this.markRead(item, Math.min(index, MAX_STAGGERED) * STAGGER_MS)))
  }

  // Hauteur figée en pixels puis ramenée à 0 : une transition ne part pas de `auto`.
  async collapse(element) {
    element.style.height = `${element.offsetHeight}px`
    element.offsetHeight // reflow : la hauteur de départ doit être appliquée avant la classe
    element.classList.add("is-collapsing")
    await wait(this.constructor.COLLAPSE_MS)
  }

  // --- Lecture du DOM ---------------------------------------------------------------------

  get feed() {
    return this.element.querySelector(".notifications-feed")
  }

  // Ce qu'une bascule remplace : la liste, l'état vide, ou le gabarit d'une bascule encore
  // en attente de sa réponse.
  get contentRegion() {
    return this.feed?.querySelector(":scope > .notifications-feed__list, :scope > .notifications-feed__empty")
  }

  placeholderFor(filter) {
    return this.element.querySelector(`template[data-feed-placeholder="${CSS.escape(filter)}"]`)
  }

  get itemsAndDays() {
    return [...this.element.querySelectorAll(".notifications-feed__day-row, .notifications-feed__item, .notifications-feed__loading")]
  }

  get unreadItems() {
    return [...this.element.querySelectorAll(".notifications-feed__item--unread")]
  }

  get reducedMotion() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  }
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
